import { z } from "zod";

/**
 * LA CLE D'UNE CONVERSATION, et l'id qui en derive (contrat conversations du
 * 05/10/2026, `docs/socle/conversations.md`).
 *
 * Un message direct entre les memes personnes, le chatter d'une meme fiche :
 * il n'en existe qu'UN par org. Plutot qu'une requete « existe-t-il deja ? »
 * suivie d'une creation (deux clients qui ecrivent en meme temps en font
 * deux), la cle est DETERMINISTE et l'id du document en derive : retrouver la
 * conversation est un `get`, et deux creations concurrentes visent le meme
 * document.
 *
 *   - `direct:p1|p2`           les personIds TRIES, joints par `|`
 *   - `fiche:{family}/{recordId}`   le chatter d'une fiche
 *   - `porte:{appId}/{doorId}`      le chatter d'une porte
 *   - `app:{appId}`                 le chatter d'une app
 *
 * Un canal cree par le SDK a la cle `canal:<cle du nom>` (`cleDeCanal`) : deux
 * canaux ne portent pas le meme nom (casse et accents ignores).
 *
 * `conversationIdDe` est ASYNCHRONE (SHA-256 par Web Crypto, present dans le
 * navigateur et dans Node) ; le SDK l'appelle, l'ecran ne la voit pas.
 */

export const CONVERSATION_TYPES = ["canal", "direct", "chatter"] as const;

export const CONVERSATION_CLE_MAX = 300;
export const cleSchema = z.string().min(1).max(CONVERSATION_CLE_MAX);

/** L'ancre d'un chatter de porte ou d'app, telle qu'elle est stockee (`ancre` de la conversation). */
export const ancreDAppSchema = z
  .object({ appId: z.string().min(1).max(64), doorId: z.string().min(1).max(64).optional() })
  .strict();
export type AncreDApp = z.infer<typeof ancreDAppSchema>;

/** L'ancre d'une fiche (celle de `acces.ancre`). */
export const ancreDeFicheSchema = z.object({ family: z.string().min(1), recordId: z.string().min(1) }).strict();
export type AncreDeFiche = z.infer<typeof ancreDeFicheSchema>;

/** Ce qu'un chatter rattache : une fiche, ou une porte ou une app. */
export const ancreDeConversationSchema = z.union([ancreDeFicheSchema, ancreDAppSchema]);
export type AncreDeConversation = z.infer<typeof ancreDeConversationSchema>;

export function estAncreDeFiche(ancre: AncreDeConversation): ancre is AncreDeFiche {
  return "family" in ancre;
}

/** Le separateur des personIds d'une cle de direct (jamais present dans un personId). */
export const CLE_DIRECT_SEPARATEUR = "|";

/** La cle d'un direct entre ces personnes : triee, sans doublon, deux personnes au moins. */
export function cleDeDirect(personIds: readonly string[]): string {
  const ids = [...new Set(personIds)].filter((id) => typeof id === "string" && id.length > 0).sort();
  if (ids.length < 2) {
    throw new Error("cleDeDirect : un message direct reunit au moins deux personnes.");
  }
  for (const id of ids) {
    if (id.includes(CLE_DIRECT_SEPARATEUR) || id.includes(":")) {
      throw new Error(`cleDeDirect : personId invalide (${id}).`);
    }
  }
  return `direct:${ids.join(CLE_DIRECT_SEPARATEUR)}`;
}

/** La cle d'un chatter : par fiche, par porte ou par app. */
export function cleDeChatter(ancre: AncreDeConversation): string {
  if (estAncreDeFiche(ancre)) {
    ancreDeFicheSchema.parse(ancre);
    return `fiche:${ancre.family}/${ancre.recordId}`;
  }
  ancreDAppSchema.parse(ancre);
  return ancre.doorId !== undefined ? `porte:${ancre.appId}/${ancre.doorId}` : `app:${ancre.appId}`;
}

/** Les personIds d'une cle de direct, ou `null` si ce n'en est pas une. */
export function personnesDeCleDirect(cle: string): string[] | null {
  if (!cle.startsWith("direct:")) return null;
  return cle.slice("direct:".length).split(CLE_DIRECT_SEPARATEUR);
}

async function sha256Hex(texte: string): Promise<string> {
  const octets = new TextEncoder().encode(texte);
  const empreinte = await globalThis.crypto.subtle.digest("SHA-256", octets);
  return [...new Uint8Array(empreinte)].map((n) => n.toString(16).padStart(2, "0")).join("");
}

/** L'id du document d'une conversation a cle : `c-` + 32 hexadecimaux de SHA-256(orgId, cle). */
export async function conversationIdDe(orgId: string, cle: string): Promise<string> {
  if (!orgId || !cle) throw new Error("conversationIdDe : orgId et cle sont requis.");
  return `c-${(await sha256Hex(`${orgId}\u0000${cle}`)).slice(0, 32)}`;
}

/**
 * LA CLE D'UN NOM DE CANAL (vague du soir, point e) : minuscules, sans accent,
 * espaces ramasses, sans le `#` de tete. « Général », « general » et
 * « GENERAL » ont la meme cle : un nom de canal est unique dans l'org par cette
 * cle. L'id d'un canal cree par le SDK en derive (`cleDeCanal`).
 */
export function cleDeNomDeCanal(nom: string): string {
  return nom
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/^#+/, "")
    .toLocaleLowerCase("fr")
    .replace(/\s+/g, " ")
    .trim();
}

/** La cle de conversation d'un canal : `canal:<cle du nom>` ; l'id du document en derive. */
export function cleDeCanal(nom: string): string {
  const cle = cleDeNomDeCanal(nom);
  if (cle === "") throw new Error("cleDeCanal : un canal porte un nom.");
  return `canal:${cle}`;
}
